import { useState, useEffect, useCallback } from 'react'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { UserPlus, Shield, GraduationCap, Check, X, Trash2, Mail, Inbox, Crown, Send, Ban } from 'lucide-react'
import { format } from 'date-fns'
import { UserProfile } from '../types'
import { ALL_ROLES, ROLE_DESCRIPTIONS, Role, hasAdminRights, invitableRoles, isSuperAdmin, roleLabel } from '../lib/roles'

type Invitation = {
  id: string
  email: string
  name: string | null
  role: Role
  status: 'pending' | 'accepted' | 'revoked'
  createdAt: string
}

type InviteMethod = 'email' | 'password'

const ROLE_BADGE: Record<Role, { cls: string; Icon: typeof Shield }> = {
  super_admin: { cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200', Icon: Crown },
  admin: { cls: 'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200', Icon: Shield },
  intake: { cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', Icon: Inbox },
  tutor: { cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', Icon: GraduationCap },
}

function RoleBadge({ role }: { role: Role }) {
  const { cls, Icon } = ROLE_BADGE[role] ?? ROLE_BADGE.tutor
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3 mr-1" />
      {roleLabel(role)}
    </span>
  )
}

const likeExact = (text: string) => text.replace(/[\\%_]/g, c => `\\${c}`)

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white'

export function UserManagement() {
  const { profile } = useAuth()
  const superAdmin = isSuperAdmin(profile?.role)
  const allowedRoles = invitableRoles(profile?.role)

  const [users, setUsers] = useState<UserProfile[]>([])
  const [invites, setInvites] = useState<Invitation[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchingUsers, setFetchingUsers] = useState(true)
  const [roleSavingId, setRoleSavingId] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'tutor' as Role,
    method: 'email' as InviteMethod,
  })

  const fetchUsers = useCallback(async () => {
    try {
      setFetchingUsers(true)
      const [{ data, error }, inv] = await Promise.all([
        supabase.from('profiles').select('*').order('created_at', { ascending: false }),
        supabase.from('invitations').select('*').eq('status', 'pending').order('created_at', { ascending: false }),
      ])
      if (error) throw error
      setUsers((data ?? []).map(user => ({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        avatarUrl: user.avatar_url,
        createdAt: user.created_at
      })))
      setInvites((inv.data ?? []).map((r: any) => ({
        id: r.id, email: r.email, name: r.name, role: r.role, status: r.status, createdAt: r.created_at,
      })))
    } catch (error) {
      console.error('Error fetching users:', error)
      setMessage({ type: 'error', text: 'Failed to fetch users' })
    } finally {
      setFetchingUsers(false)
    }
  }, [])

  useEffect(() => {
    if (hasAdminRights(profile?.role)) fetchUsers()
  }, [profile?.role, fetchUsers])

  const canDelete = (user: UserProfile) => user.id !== profile?.id && (superAdmin || user.role === 'tutor')

  const handleDeleteUser = async (user: UserProfile) => {
    const who = user.name || user.email
    if (!window.confirm(
      `Delete ${who}'s account?

` +
      `• They can no longer sign in.
` +
      `• Their past sessions and students stay.
` +
      (user.role === 'tutor' ? `• Their tutor record is switched off, so signing up again with this email gives no access. Re-invite them to restore it.
` : '') +
      `
This cannot be undone.`
    )) {
      return
    }

    try {
      const { error } = await supabase.rpc('delete_user_account', { p_user_id: user.id })
      if (error) throw error

      setMessage({ type: 'success', text: `${who}'s account was deleted.` })
      fetchUsers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete user.' })
    }
  }

  const handleChangeRole = async (user: UserProfile, role: Role) => {
    if (role === user.role) return
    if (!window.confirm(`Change ${user.name || user.email} from ${roleLabel(user.role)} to ${roleLabel(role)}?`)) return
    setRoleSavingId(user.id)
    setMessage(null)
    try {
      const { error } = await supabase.from('profiles').update({ role }).eq('id', user.id)
      if (error) throw error
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, role } : u)))
      setMessage({ type: 'success', text: `${user.name || user.email} is now ${roleLabel(role)}.` })
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to change role' })
    } finally {
      setRoleSavingId(null)
    }
  }

  const ensureTutorRecord = async (name: string, email: string) => {
    const { data: existing, error: lookupError } = await supabase.from('tutors').select('id').ilike('email', likeExact(email)).limit(1)
    if (lookupError) throw lookupError
    if (existing && existing.length > 0) return
    const { data: newTutor, error: tutorError } = await supabase
      .from('tutors')
      .insert({ name, email, subjects: ['General'] })
      .select('id')
      .single()
    if (tutorError) {
      console.error('Error creating tutor record:', tutorError)
    } else if (newTutor) {
      // Hourly rate is money — lives in the admin-only tutor_billing table.
      const { error: billingError } = await supabase
        .from('tutor_billing')
        .insert({ tutor_id: newTutor.id, hourly_rate: 50 })
      if (billingError) console.error('Error creating tutor billing:', billingError)
    }
  }

  const recordInvitation = async (email: string, name: string, role: Role): Promise<string> => {
    await supabase.from('invitations').update({ status: 'revoked' }).ilike('email', likeExact(email)).eq('status', 'pending')
    const { data, error } = await supabase.from('invitations').insert({ email, name, role }).select('id').single()
    if (error) throw error
    return data.id
  }

  // Sent through Resend by the invite-user edge function, with a sign-in link in it.
  const sendInviteEmail = async (invitationId: string) => {
    const { error } = await supabase.functions.invoke('invite-user', {
      body: { invitationId, redirectTo: `${window.location.origin}/dashboard/profile?welcome=1` },
    })
    if (error) {
      const body = error instanceof FunctionsHttpError ? await error.context.json().catch(() => null) : null
      throw new Error(body?.error || error.message)
    }
  }

  const handleCreateUser = async () => {
    setLoading(true)
    setMessage(null)
    const email = formData.email.trim().toLowerCase()
    const name = formData.name.trim()
    const role = formData.role

    try {
      if (!allowedRoles.includes(role)) throw new Error(`You can't invite a ${roleLabel(role)}.`)
      if (users.some(u => u.email?.toLowerCase() === email)) {
        throw new Error(`${email} already has an account. ${superAdmin ? 'Change their role in the list instead.' : 'Ask a super admin to change their role.'}`)
      }

      const invitationId = await recordInvitation(email, name, role)
      if (role === 'tutor') await ensureTutorRecord(name, email)

      if (formData.method === 'email') {
        try {
          await sendInviteEmail(invitationId)
          setMessage({ type: 'success', text: `Invitation sent to ${email} as ${roleLabel(role)}. They'll get an email with a sign-in link.` })
        } catch (emailError: any) {
          // The invitation is saved and listed below, so it can be sent again from there.
          setMessage({
            type: 'error',
            text: `Invitation saved, but the email to ${email} couldn't be sent (${emailError.message}). Use "Resend email" below to try again.`,
          })
        }
      } else {
        const { data: { session: currentSession } } = await supabase.auth.getSession()
        if (!currentSession) throw new Error('No active admin session found')

        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password: formData.password,
          options: { data: { name } }
        })
        if (authError) throw authError

        if (authData.user) {
          const { error: profileError } = await supabase
            .from('profiles')
            .upsert({ id: authData.user.id, email, name }, { onConflict: 'id' })
          if (profileError) console.error('Profile creation error:', profileError)
        }

        const { error: restoreError } = await supabase.auth.setSession({
          access_token: currentSession.access_token,
          refresh_token: currentSession.refresh_token
        })
        if (restoreError) {
          console.error('Error restoring admin session:', restoreError)
          throw new Error('User created but failed to restore your admin session. Please refresh the page.')
        }
        setMessage({ type: 'success', text: `${roleLabel(role)} account created for ${email}.` })
      }

      setFormData({ email: '', password: '', name: '', role: 'tutor', method: 'email' })
      setShowCreateModal(false)
      fetchUsers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to invite user' })
    } finally {
      setLoading(false)
    }
  }

  // Each email has a new link and the previous one stops working, so one send at a time.
  const handleResend = async (inv: Invitation) => {
    if (resendingId) return
    setResendingId(inv.id)
    setMessage(null)
    try {
      await sendInviteEmail(inv.id)
      setMessage({ type: 'success', text: `Invitation email sent again to ${inv.email}. Only the link in this newest email works.` })
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to resend' })
    } finally {
      setResendingId(null)
    }
  }

  const handleRevoke = async (inv: Invitation) => {
    if (!window.confirm(`Cancel the invitation for ${inv.email}?`)) return
    setMessage(null)
    const { error } = await supabase.from('invitations').update({ status: 'revoked' }).eq('id', inv.id)
    if (error) {
      setMessage({ type: 'error', text: error.message })
      return
    }
    setInvites(prev => prev.filter(i => i.id !== inv.id))
  }

  if (!hasAdminRights(profile?.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Admin Access Only</h2>
          <p className="text-gray-600 dark:text-gray-400">You need admin privileges to manage users.</p>
        </div>
      </div>
    )
  }

  const canSubmit = !!formData.email && !!formData.name && (formData.method === 'email' || formData.password.length >= 6)

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {superAdmin
              ? 'Invite people and set their role.'
              : 'Invite tutors. Only a super admin can invite office staff or change roles.'}
          </p>
        </div>
        <button
          onClick={() => { setMessage(null); setShowCreateModal(true) }}
          className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800 transition-colors"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Invite user
        </button>
      </div>

      {/* Message Alert */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center ${
          message.type === 'success'
            ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
            : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
        }`}>
          {message.type === 'success' ? <Check className="h-5 w-5 mr-2 flex-shrink-0" /> : <X className="h-5 w-5 mr-2 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      {invites.length > 0 && (
        <div className="mb-6 bg-white dark:bg-surface-100 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Mail className="h-5 w-5" /> Pending invitations
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">They get this role the first time they sign in.</p>
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {invites.map(inv => {
              const canManage = superAdmin || inv.role === 'tutor'
              return (
                <li key={inv.id} className="px-6 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{inv.name || inv.email}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {inv.email} · invited {format(new Date(inv.createdAt), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <RoleBadge role={inv.role} />
                    {canManage && (
                      <>
                        <button
                          onClick={() => handleResend(inv)}
                          disabled={resendingId !== null}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send className="h-3 w-3" /> {resendingId === inv.id ? 'Sending…' : 'Resend email'}
                        </button>
                        <button
                          onClick={() => handleRevoke(inv)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md text-gray-500 hover:text-red-500"
                        >
                          <Ban className="h-3 w-3" /> Cancel
                        </button>
                      </>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white dark:bg-surface-100 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">All Users</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {users.length} {users.length === 1 ? 'user' : 'users'} registered in the system
          </p>
        </div>

        {fetchingUsers ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No users found. Invite your first user to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-surface-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-surface-100 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-surface-0">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center">
                            <span className="text-brand-700 dark:text-brand-300 font-medium text-sm">
                              {user.name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name || 'No name'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {superAdmin && user.id !== profile?.id ? (
                        <select
                          value={user.role}
                          disabled={roleSavingId === user.id}
                          onChange={(e) => handleChangeRole(user, e.target.value as Role)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-surface-0 text-gray-900 dark:text-white disabled:opacity-50"
                        >
                          {ALL_ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                        </select>
                      ) : (
                        <RoleBadge role={user.role} />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.createdAt ? format(new Date(user.createdAt), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDeleteUser(user)}
                        disabled={!canDelete(user)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={
                          user.id === profile?.id
                            ? 'You cannot delete your own account'
                            : canDelete(user)
                              ? 'Delete account'
                              : 'Only a super admin can delete this account'
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-100 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Invite user</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={inputCls}
                  placeholder="Jana Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={inputCls}
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as Role })}
                  className={inputCls}
                >
                  {allowedRoles.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{ROLE_DESCRIPTIONS[formData.role]}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">How</label>
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 dark:bg-surface-0 p-1">
                  {(['email', 'password'] as InviteMethod[]).map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setFormData({ ...formData, method: m })}
                      className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                        formData.method === m
                          ? 'bg-white dark:bg-surface-100 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {m === 'email' ? 'Email a sign-in link' : 'Set a password now'}
                    </button>
                  ))}
                </div>
              </div>

              {formData.method === 'password' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={inputCls}
                    placeholder="Minimum 6 characters"
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setMessage(null)
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={loading || !canSubmit}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Working...' : formData.method === 'email' ? 'Send invitation' : 'Create account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
